/**
 * Privacy policy modal component that displays the privacy policy and handles user acceptance.
 * Features a responsive design with the application's theme colors.
 */

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

/**
 * PrivacyPolicyModal component that displays the privacy policy in a modal dialog.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is currently open
 * @param {Function} props.onAccept - Callback function called when user accepts the policy
 * @returns {JSX.Element} The rendered PrivacyPolicyModal component
 */
const PrivacyPolicyModal = ({ isOpen, onAccept }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#cabb78] to-[#b5a666] px-6 py-4 rounded-t-xl">
          <h2 className="text-2xl font-bold text-white">
            {t("Privacy_policy")}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="prose prose-sm max-w-none mb-6 text-gray-700">
            <p className="mb-4">{t("privacy_policy_content")}</p>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("data_collection")}
              </h3>
              <p>{t("data_collection_content")}</p>

              <h3 className="text-lg font-semibold text-gray-800">
                {t("data_usage")}
              </h3>
              <p>{t("data_usage_content")}</p>

              <h3 className="text-lg font-semibold text-gray-800">
                {t("data_protection")}
              </h3>
              <p>{t("data_protection_content")}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              onClick={onAccept}
              className="px-6 py-2 bg-gradient-to-r from-[#cabb78] to-[#b5a666] text-white rounded-lg 
                       hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 
                       transform hover:scale-105 shadow-md hover:shadow-lg font-medium"
            >
              {t("accept_policy")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

PrivacyPolicyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
};

export default PrivacyPolicyModal;
